import { TMReceivedProductAPI } from '@business/api/tm_received_product';
import { TimeMaterialId } from '@business/entities/tm';
import { TMReceivedProductResponse, TMReceivedProductId, TMReceivedProductSearchParams } from '@business/entities/tm/TMReceivedProductResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';
import { ProductFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/ReceiveProductsModal/ReceiveProductsModal';

export class TMReceivedProductBloc extends BaseBloc {
  private readonly timeTrackingRepo = new Repository<TMReceivedProductResponse>({
    getItemId: item => item.id,
  });

  readonly timeTrackings$ = this.timeTrackingRepo.items$;
  readonly selectReceivedProduct = this.timeTrackingRepo.selectItem;

  readonly selectReceivedProductByOrderId = (orderId: TimeMaterialId) =>
    this.timeTrackingRepo.items$.pipe(map(items => items.filter(item => item.orderId == orderId)));

  readonly selectReceivedProductByProductItem = (estimateLineId: number) =>
    this.timeTrackingRepo.items$.pipe(map(items => items.filter(item => item.estimateItemId == estimateLineId)));

  onReset() {
    this.timeTrackingRepo.reset();
  }

  fetchReceivedProducts = (data?: TMReceivedProductSearchParams) => {
    return TMReceivedProductAPI.fetchReceivedProducts(data).pipe(
      this.timeTrackingRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  fetchReceivedProduct = (id: TMReceivedProductId) => {
    return TMReceivedProductAPI.fetchReceivedProduct(id).pipe(
      this.timeTrackingRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  createReceivedProduct = (data: Partial<ProductFormData>) => {
    return TMReceivedProductAPI.createReceivedProduct(data).pipe(
      this.timeTrackingRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_time_tracking'),
      }),
    );
  };

  updateReceivedProduct = (id: TMReceivedProductId, data: Partial<ProductFormData>) => {
    return TMReceivedProductAPI.updateReceivedProduct(id, data).pipe(
      this.timeTrackingRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_time_tracking'),
      }),
    );
  };

  deleteReceivedProduct = (id: TMReceivedProductId) => {
    return showDeleteConfirmation('Delete Time Tracking', 'Do you really want to remove this tracked time?').pipe(
      switchMap(() => TMReceivedProductAPI.deleteReceivedProduct(id)),
      this.timeTrackingRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_time_tracking'),
      }),
    );
  };
}
